"use client";

import { useLanguage } from "@/i18n/LanguageContext";

const criteriaEn = [
  {
    title: "Storm Protection",
    impact: "Always-on laminated glass resists debris at 150+ mph without any action required before a storm. No deployment timeline, no human error.",
    impactVerdict: "WINS",
    shutters: "Require manual or motorized deployment before each storm. Roll-up and accordion styles are faster; fabric and panel styles take 1–3 hours for an average home.",
    shuttersVerdict: "TRADE-OFF",
  },
  {
    title: "Daily Experience",
    impact: "Clear views, natural light, and silent operation year-round. No storage, no seasonal maintenance, no aesthetic compromise.",
    impactVerdict: "WINS",
    shutters: "When retracted, roll-ups and accordions still partially obstruct views. Fabric panels must be stored. All shutter types reduce light when deployed.",
    shuttersVerdict: "TRADE-OFF",
  },
  {
    title: "Up-Front Cost",
    impact: "$850 – $1,400 per window installed. A 10-window home runs $9,200 – $16,500. Grants and financing can reduce net out-of-pocket.",
    impactVerdict: "TRADE-OFF",
    shutters: "$45 – $95 per square foot installed. A 10-window home runs $4,500 – $9,000. Lower barrier for budget-conscious owners.",
    shuttersVerdict: "WINS",
  },
  {
    title: "Insurance Discount",
    impact: "15% – 30% annual premium reduction. Insurers treat impact windows as permanent wind mitigation. Discount applies 365 days per year.",
    impactVerdict: "WINS",
    shutters: "5% – 15% if the shutters are NOA-approved and properly installed. Discount only applies when shutters are deployed; some carriers require photographic proof.",
    shuttersVerdict: "TRADE-OFF",
  },
  {
    title: "Property Value",
    impact: "Improves home resale value. Buyers actively request impact windows in listings.",
    impactVerdict: "WINS",
    shutters: "Shutters are viewed as functional add-ons rather than permanent structural upgrades. Less buyer interest at resale.",
    shuttersVerdict: "TRADE-OFF",
  },
  {
    title: "Longevity",
    impact: "Frames and glass rated for 30+ years with minimal maintenance beyond occasional cleaning.",
    impactVerdict: "WINS",
    shutters: "10 – 15 years typical lifespan. Motors, tracks, and fabric panels degrade in Miami's UV and salt-air environment. Replacement parts add cost.",
    shuttersVerdict: "TRADE-OFF",
  },
];

const criteriaEs = [
  {
    title: "Protección contra Tormentas",
    impact: "El vidrio laminado siempre activo resiste escombros a 150+ mph sin necesidad de acción antes de una tormenta. Sin plazos de despliegue, sin error humano.",
    impactVerdict: "GANA",
    shutters: "Requieren despliegue manual o motorizado antes de cada tormenta. Los estilos de enrollar y acordeón son más rápidos; los de tela y panel toman 1-3 horas para una casa promedio.",
    shuttersVerdict: "COMPENSACIÓN",
  },
  {
    title: "Experiencia Diaria",
    impact: "Vistas claras, luz natural y operación silenciosa durante todo el año. Sin almacenamiento, sin mantenimiento estacional, sin compromiso estético.",
    impactVerdict: "GANA",
    shutters: "Cuando se retraen, los enrrollables y acordeones aún obstruyen parcialmente las vistas. Los paneles de tela deben almacenarse. Todos los tipos de persianas reducen la luz cuando están desplegados.",
    shuttersVerdict: "COMPENSACIÓN",
  },
  {
    title: "Costo Inicial",
    impact: "$850 – $1,400 por ventana instalada. Una casa de 10 ventanas cuesta $9,200 – $16,500. Subvenciones y financiamiento pueden reducir el costo neto.",
    impactVerdict: "COMPENSACIÓN",
    shutters: "$45 – $95 por pie cuadrado instalado. Una casa de 10 ventanas cuesta $4,500 – $9,000. Menor barrera para propietarios con presupuesto ajustado.",
    shuttersVerdict: "GANA",
  },
  {
    title: "Descuento de Seguro",
    impact: "Reducción de prima anual del 15% – 30%. Los aseguradores tratan las ventanas de impacto como mitigación de viento permanente. El descuento aplica 365 días al año.",
    impactVerdict: "GANA",
    shutters: "5% – 15% si las persianas tienen aprobación NOA y están correctamente instaladas. El descuento solo aplica cuando las persianas están desplegadas; algunas aseguradoras requieren prueba fotográfica.",
    shuttersVerdict: "COMPENSACIÓN",
  },
  {
    title: "Valor de la Propiedad",
    impact: "Mejora el valor de reventa de la vivienda. Los compradores solicitan activamente ventanas de impacto en los listados.",
    impactVerdict: "GANA",
    shutters: "Las persianas se ven como complementos funcionales en lugar de mejoras estructurales permanentes. Menos interés de compradores en la reventa.",
    shuttersVerdict: "COMPENSACIÓN",
  },
  {
    title: "Durabilidad",
    impact: "Marcos y vidrio con clasificación de 30+ años con mantenimiento mínimo más allá de limpieza ocasional.",
    impactVerdict: "GANA",
    shutters: "10 – 15 años de vida útil típica. Motores, rieles y paneles de tela se degradan con el UV y aire salino de Miami. Las piezas de repuesto aumentan el costo.",
    shuttersVerdict: "COMPENSACIÓN",
  },
];

export default function CriteriaSections() {
  const { currentLang } = useLanguage();
  const isEs = currentLang === "es";
  const criteria = isEs ? criteriaEs : criteriaEn;

  return (
    <section className="py-10 space-y-12">
      {criteria.map((c) => (
        <div key={c.title}>
          <h3 className="text-2xl font-bold text-[#064e3b] mb-5">{c.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="bg-[#047857]/10 border-l-4 border-[#047857] p-6">
              <p className="text-xs uppercase tracking-widest text-[#047857] font-bold mb-2">
                {isEs ? "Ventanas de Impacto" : "Impact Windows"}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">{c.impact}</p>
              <span
                className={`inline-block mt-3 text-xs font-bold px-3 py-1 rounded-full ${
                  (c.impactVerdict === "WINS" || c.impactVerdict === "GANA")
                    ? "bg-[#047857] text-white"
                    : "bg-[#fbbf24] text-[#064e3b]"
                }`}
              >
                {c.impactVerdict}
              </span>
            </div>
            <div className="bg-[#ea580c]/10 border-l-4 border-[#ea580c] p-6">
              <p className="text-xs uppercase tracking-widest text-[#ea580c] font-bold mb-2">
                {isEs ? "Persianas de Huracán" : "Hurricane Shutters"}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">{c.shutters}</p>
              <span
                className={`inline-block mt-3 text-xs font-bold px-3 py-1 rounded-full ${
                  (c.shuttersVerdict === "WINS" || c.shuttersVerdict === "GANA")
                    ? "bg-[#ea580c] text-white"
                    : "bg-[#fefce8] text-[#064e3b] border border-[#064e3b]/20"
                }`}
              >
                {c.shuttersVerdict}
              </span>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}